// ── guestAdd — "add first, sign up when it counts", from any door ─────────────
// The /start walkthrough mints an anonymous session and lets a visitor build a
// library with no account (the load-bearing "guest library = app user" model,
// 16.208). But the direct doors never got that: a visitor who lands on Discover
// or a show page from a Reddit link or the Play Store and taps Add hit a "Sign
// in to track shows" wall and left. Measured 2026-09-26: both new visitors that
// day (one Play install, one from r/TVTime) went /discover → /show → gone, 0
// shows. This is the missing door: start (or reuse) an anonymous session, write
// the row exactly the way /start does, and tell useAuth the guest now owns a
// library so the app unlocks in place. Pure; the client is injected for tests.
// analytics.js pulls in the Supabase client (an extension-less Vite import), which
// Node's test runner cannot resolve, so it is loaded lazily and injectable.
const lazyLog = (event, props) => import('./analytics.js').then(m => m.logEvent(event, props)).catch(() => {})

const inflight = new Map()
let minting = null

async function sessionUid(supabase) {
  try {
    const { data } = await supabase.auth.getSession()
    if (data?.session?.user?.id) return data.session.user.id
  } catch { /* fall through to a fresh anonymous session */ }
  try {
    if (!minting) minting = Promise.resolve(supabase.auth.signInAnonymously()).finally(() => { minting = null })
    const { data, error } = await minting
    return error ? null : (data?.user?.id || null)
  } catch { return null }
}

export function guestAddShow(show, opts = {}) {
  if (!show?.id || !opts.supabase) return Promise.resolve({ ok: false, reason: 'bad-input' })
  if (inflight.has(show.id)) return inflight.get(show.id)
  const p = guestAddOnce(show, opts).finally(() => inflight.delete(show.id))
  inflight.set(show.id, p)
  return p
}

async function guestAddOnce(show, { supabase, dispatch = null, log = lazyLog }) {
  const uid = await sessionUid(supabase)
  if (!uid) return { ok: false, reason: 'no-session' }
  const { error } = await supabase.from('watchlist').upsert({
    user_id: uid, show_id: show.id, show_data: show, status: 'plan_to_watch',
  })
  if (error) return { ok: false, reason: 'write-failed' }
  const src = (typeof location !== 'undefined' ? location.pathname : '').replace(/^(\/show|\/aftershow|\/canon|\/curator|\/u)\/.*$/, '$1/*')
  // Same funnel events wl.add() writes, so a guest door add counts in every read:
  // show_added (with the route), and the once-per-device activation moment.
  log('show_added', { first: true, src, guest: true })
  try {
    if (!localStorage.getItem('nextep_first_show_logged')) {
      localStorage.setItem('nextep_first_show_logged', '1')
      log('first_show_added')
    }
  } catch { /* storage blocked: skip the once-guard rather than risk a dupe */ }
  const emit = dispatch || ((name, detail) => { try { window.dispatchEvent(new CustomEvent(name, { detail })) } catch { /* noop */ } })
  // useAuth listens for this, busts its "no library" cache and promotes the session
  // to an app user; useWatchlist then loads the row. No reload, no wall.
  emit('nextep:guest-library-changed', { show_id: show.id })
  emit('nextep:toast', {
    title: `Added ${show.name || 'it'}`, accent: 'var(--amber)',
    body: 'Your list lives on this device for now. Sign in any time to keep it everywhere.',
  })
  return { ok: true, userId: uid }
}
